import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';
import { calculateDepositStatus } from '../../services/texasComplianceService';

export const FinanceExports: React.FC = () => {
  const [deposits, setDeposits] = useState<any[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [depRes, regRes] = await Promise.allSettled([
        texasApi.deposits.getAll(),
        texasApi.complianceDashboard.getPortfolio(),
      ]);
      if (depRes.status === 'fulfilled') {
        const d: any = depRes.value;
        setDeposits(Array.isArray(d) ? d : Array.isArray(d?.deposits) ? d.deposits : []);
      }
      if (regRes.status === 'fulfilled') {
        const d = regRes.value;
        setRegistrations(d?.registrationStatus ? Object.entries(d.registrationStatus).map(([k, v]) => ({ type: k, count: v })) : []);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const totalDeposits = deposits.reduce((sum: number, d: any) => sum + (d.depositAmount || 0), 0);
  const totalRefunded = deposits.filter((d: any) => d.refundStatus === 'refunded').length;
  const totalPending = deposits.filter((d: any) => d.refundStatus === 'pending').length;
  const totalDisputed = deposits.filter((d: any) => d.refundStatus === 'disputed').length;

  const handleExport = async () => {
    try {
      const summary = {
        generatedAt: new Date().toISOString(),
        totalDeposits,
        totalRefunded,
        totalPending,
        totalDisputed,
        deposits: deposits.map((d: any) => ({
          tenant: d.tenantName,
          property: d.propertyName,
          amount: d.depositAmount,
          status: d.refundStatus,
          daysRemaining: d.daysRemaining,
        })),
      };
      const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Export failed');
    }
  };

  const csvEscape = (value: any): string => {
    const s = value === null || value === undefined ? '' : String(value);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExportCsv = () => {
    try {
      const headers = ['Tenant', 'Property', 'Amount', 'Status', 'Days Remaining'];
      const rows = deposits.map((d: any) => [
        d.tenantName,
        d.propertyName,
        d.depositAmount,
        d.refundStatus,
        d.daysRemaining,
      ]);
      const csv = [headers, ...rows]
        .map((row) => row.map(csvEscape).join(','))
        .join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Export failed');
    }
  };

  return (
    <div data-testid="finance-exports" className="texas-page-container">
      <h1>Finance Exports</h1>
      <p className="page-description">
        Financial data exports — deposit summaries, registration fees, and refund tracking.
      </p>

      {error && <div className="error" data-testid="error">{error}</div>}

      {!loading && (
        <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-slate-500 tracking-wider">TOTAL VALUE</div>
            <div className="text-3xl font-black text-slate-900 mt-1">${totalDeposits.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-emerald-600 tracking-wider">REFUNDED</div>
            <div className="text-3xl font-black text-emerald-600 mt-1">{totalRefunded}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-amber-600 tracking-wider">PENDING</div>
            <div className="text-3xl font-black text-amber-600 mt-1">{totalPending}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-red-600 tracking-wider">DISPUTED</div>
            <div className="text-3xl font-black text-red-600 mt-1">{totalDisputed}</div>
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <h2 className="text-lg font-bold mb-3">Export Actions</h2>
        <button
          onClick={handleExport}
          disabled={loading || deposits.length === 0}
          data-testid="export-json-btn"
          className="px-4 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50"
        >
          Export JSON Summary
        </button>
        <button
          onClick={handleExportCsv}
          disabled={loading || deposits.length === 0}
          data-testid="export-csv-btn"
          className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 ml-2"
        >
          Export CSV
        </button>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Deposit Summary ({deposits.length} items)</h2>
        {loading ? (
          <div className="no-results">Loading finance data...</div>
        ) : deposits.length === 0 ? (
          <div className="no-results">No deposit data found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-2">Tenant</th>
                  <th className="text-left p-2">Property</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-center p-2">Status</th>
                  <th className="text-right p-2">Days Left</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((dep: any) => (
                  <tr key={dep.id} className="border-b border-gray-100">
                    <td className="p-2">{dep.tenantName}</td>
                    <td className="p-2">{dep.propertyName}</td>
                    <td className="p-2 text-right">${(dep.depositAmount || 0).toLocaleString()}</td>
                    <td className="p-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        dep.refundStatus === 'refunded' ? 'bg-emerald-100 text-emerald-700' :
                        dep.refundStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                        dep.refundStatus === 'disputed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {dep.refundStatus || 'pending'}
                      </span>
                    </td>
                    <td className="p-2 text-right">{dep.daysRemaining ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default FinanceExports;
